import { useQuery } from '@rocicorp/zero/react';
import {
  normalizeZeroRow,
  queries,
  queryTableName,
} from '@strength/zero-schema';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { SESSION_QUERY_KEY } from '../../auth';
import {
  isRoleSyncComplete,
  shouldRefreshSession,
  viewerFingerprint,
} from './role-sync';

// Inside ZeroProvider: watches the viewer role queries and, on a definitive
// role/team change, refreshes the session — the updated auth then re-keys the
// store (see ZeroClientProvider + role-storage-key). Renders nothing.
export function ZeroSessionSync() {
  const queryClient = useQueryClient();
  const lastSeenFingerprint = useRef<string | null>(null);

  const profileRoleQuery = queries.viewer.profileRole();
  const teamMembershipsQuery = queries.viewer.teamMemberships();
  const orgDefaultRoleQuery = queries.viewer.orgDefaultRole();

  const [profileRoleRows, profileRoleDetails] = useQuery(profileRoleQuery);
  const [teamMembershipRows, teamMembershipsDetails] =
    useQuery(teamMembershipsQuery);
  const [orgDefaultRoleRows, orgDefaultRoleDetails] =
    useQuery(orgDefaultRoleQuery);

  // Raw useQuery bypasses useZeroQuery's normalization, so rows are run
  // through the same shared normalizer here — every Zero read must look like
  // the tRPC read for the same row (SC-5647). The table name is derived from
  // the query object so renamed queries cannot drift; when it cannot be
  // determined, normalizeZeroRow falls back to the union of all synced
  // tables' date/timestamp columns.
  const normalizeRows = <T extends Record<string, unknown>>(
    rows: readonly T[] | undefined,
    query: unknown,
  ): T[] | undefined =>
    rows?.map((row) => normalizeZeroRow(row, queryTableName(query)));
  const normalizedProfileRoleRows = normalizeRows(
    profileRoleRows,
    profileRoleQuery,
  );
  const normalizedTeamMembershipRows = normalizeRows(
    teamMembershipRows,
    teamMembershipsQuery,
  );
  const normalizedOrgDefaultRoleRows = normalizeRows(
    orgDefaultRoleRows,
    orgDefaultRoleQuery,
  );

  // Computed during render (pure). `null` until all three queries have synced,
  // so the effect can key on the fingerprint string and fire only when the
  // viewer's role/team inputs actually change.
  const complete = isRoleSyncComplete(
    profileRoleDetails.type,
    teamMembershipsDetails.type,
    orgDefaultRoleDetails.type,
  );
  const fingerprint = complete
    ? viewerFingerprint(
        normalizedProfileRoleRows?.[0],
        normalizedTeamMembershipRows ?? [],
        normalizedOrgDefaultRoleRows?.[0],
      )
    : null;

  useEffect(() => {
    if (fingerprint === null) return;
    if (shouldRefreshSession(fingerprint, lastSeenFingerprint.current)) {
      queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY });
    }
    lastSeenFingerprint.current = fingerprint;
  }, [fingerprint, queryClient]);

  return null;
}
